import { useState, useRef, useEffect, useMemo, type ComponentType } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  FileText,
  ChevronDown,
  Search,
  X,
  ShieldCheck,
  ArrowRight,
  Layers,
  Zap,
  ArrowDownToLine,
  ArrowUpFromLine,
  Lock,
} from "lucide-react";
import { PDF_TOOLS, PDF_CATEGORIES } from "@/lib/pdf-catalog";
import { ES_PDF_TOOLS, ES_PDF_CATEGORIES } from "@/lib/es-pdf-catalog";
import { DE_PDF_TOOLS, DE_PDF_CATEGORIES } from "@/lib/de-pdf-catalog";
import { FR_PDF_TOOLS, FR_PDF_CATEGORIES } from "@/lib/fr-pdf-catalog";
import { PT_BR_PDF_TOOLS, PT_BR_PDF_CATEGORIES } from "@/lib/pt-br-pdf-catalog";
import type { Language } from "@/lib/i18n";
import { ToolIcon } from "@/components/tools/ToolIcon";

export interface UniversalPdfTool {
  slug: string;
  name: string;
  category: string;
  icon: string;
  featured?: boolean;
  tagline?: string;
  keywords?: string[];
}

interface PdfColumnConfig {
  title: string;
  icon: ComponentType<{ className?: string }>;
  iconColor: string;
  categoryName: string;
  tools: UniversalPdfTool[];
}

interface PdfMegaMenuTranslations {
  pdfToolkit: string;
  searchPlaceholder: string;
  allPdfTools: string;
  allCategoriesLabel: string;
  viewAll: string;
  searchResults: string;
  noResults: string;
  privacyBadge: string;
}

const PDF_MEGA_MENU_I18N: Record<Language, PdfMegaMenuTranslations> = {
  en: {
    pdfToolkit: "PDF Toolkit Pro",
    searchPlaceholder: "Search 20+ private PDF tools (e.g. merge, compress, word to pdf, sign)...",
    allPdfTools: "Browse All PDF Tools",
    allCategoriesLabel: "Browse by Category:",
    viewAll: "View all",
    searchResults: "PDF Tools matching",
    noResults: "No PDF tools found matching your search.",
    privacyBadge: "100% Private & Browser-Based",
  },
  es: {
    pdfToolkit: "Herramientas PDF",
    searchPlaceholder: "Buscar herramientas PDF (ej. unir, comprimir, word a pdf, firmar)...",
    allPdfTools: "Ver Todas las Herramientas PDF",
    allCategoriesLabel: "Por categoría:",
    viewAll: "Ver todas",
    searchResults: "Herramientas para",
    noResults: "No se encontraron herramientas con esa búsqueda.",
    privacyBadge: "100% Privado en el Navegador",
  },
  de: {
    pdfToolkit: "PDF-Werkzeuge",
    searchPlaceholder: "PDF-Tools durchsuchen (z.B. zusammenfügen, komprimieren, signieren)...",
    allPdfTools: "Alle PDF-Werkzeuge ansehen",
    allCategoriesLabel: "Nach Kategorie:",
    viewAll: "Alle ansehen",
    searchResults: "Werkzeuge für",
    noResults: "Keine PDF-Werkzeuge gefunden.",
    privacyBadge: "100% Lokal & Sicher im Browser",
  },
  fr: {
    pdfToolkit: "Outils PDF",
    searchPlaceholder: "Rechercher parmi les outils PDF (ex. fusionner, compresser, signer)...",
    allPdfTools: "Voir tous les outils PDF",
    allCategoriesLabel: "Par catégorie :",
    viewAll: "Voir tout",
    searchResults: "Outils pour",
    noResults: "Aucun outil PDF trouvé.",
    privacyBadge: "100% Privé et sans Téléversement",
  },
  "pt-BR": {
    pdfToolkit: "Ferramentas PDF",
    searchPlaceholder: "Pesquisar ferramentas PDF (ex. juntar, comprimir, converter, assinar)...",
    allPdfTools: "Ver Todas as Ferramentas PDF",
    allCategoriesLabel: "Por categoria:",
    viewAll: "Ver todas",
    searchResults: "Ferramentas para",
    noResults: "Nenhuma ferramenta encontrada.",
    privacyBadge: "100% Privado e no Navegador",
  },
};

export function getPdfData(lang: Language) {
  switch (lang) {
    case "es":
      return {
        tools: ES_PDF_TOOLS as UniversalPdfTool[],
        categories: ES_PDF_CATEGORIES as readonly string[],
        baseHref: "/es/pdf-tools",
      };
    case "de":
      return {
        tools: DE_PDF_TOOLS as UniversalPdfTool[],
        categories: DE_PDF_CATEGORIES as readonly string[],
        baseHref: "/de/pdf-tools",
      };
    case "fr":
      return {
        tools: FR_PDF_TOOLS as UniversalPdfTool[],
        categories: FR_PDF_CATEGORIES as readonly string[],
        baseHref: "/fr/pdf-tools",
      };
    case "pt-BR":
      return {
        tools: PT_BR_PDF_TOOLS as UniversalPdfTool[],
        categories: PT_BR_PDF_CATEGORIES as readonly string[],
        baseHref: "/pt-br/pdf-tools",
      };
    default:
      return {
        tools: PDF_TOOLS as UniversalPdfTool[],
        categories: PDF_CATEGORIES as readonly string[],
        baseHref: "/pdf-tools",
      };
  }
}

function getPdfColumns(
  tools: UniversalPdfTool[],
  categories: readonly string[],
): PdfColumnConfig[] {
  const getToolsForCategory = (catName: string) => {
    return tools.filter((t) => t.category === catName);
  };

  const icons = [Layers, Zap, ArrowDownToLine, ArrowUpFromLine, Lock];
  const iconColors = [
    "text-blue-500 dark:text-blue-400",
    "text-emerald-500 dark:text-emerald-400",
    "text-indigo-500 dark:text-indigo-400",
    "text-amber-500 dark:text-amber-400",
    "text-rose-500 dark:text-rose-400",
  ];

  return categories.slice(0, 5).map((catName, idx) => ({
    title: catName,
    icon: icons[idx] || Layers,
    iconColor: iconColors[idx] || "text-primary",
    categoryName: catName,
    tools: getToolsForCategory(catName),
  }));
}

interface PdfMegaMenuProps {
  lang?: Language;
}

export function PdfMegaMenu({ lang = "en" }: PdfMegaMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const t = PDF_MEGA_MENU_I18N[lang] || PDF_MEGA_MENU_I18N.en;
  const { tools, categories, baseHref } = useMemo(() => getPdfData(lang), [lang]);
  const columns = useMemo(() => getPdfColumns(tools, categories), [tools, categories]);

  // Close dropdown on route change
  useEffect(() => {
    setIsOpen(false);
    setQuery("");
  }, [pathname]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Search filter
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return tools
      .filter((tool) => {
        return (
          tool.name.toLowerCase().includes(q) ||
          tool.category.toLowerCase().includes(q) ||
          (tool.tagline && tool.tagline.toLowerCase().includes(q)) ||
          (tool.keywords && tool.keywords.some((k) => k.toLowerCase().includes(q)))
        );
      })
      .slice(0, 16);
  }, [query, tools]);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="relative inline-block">
      {/* Desktop Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`${t.pdfToolkit} Menu`}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
          isOpen
            ? "bg-secondary text-primary shadow-xs"
            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
        }`}
      >
        <FileText className="size-4 text-primary/90" />
        <span>{t.pdfToolkit}</span>
        <ChevronDown
          className={`size-3.5 text-muted-foreground/80 transition-transform duration-200 ${
            isOpen ? "rotate-180 text-primary" : ""
          }`}
        />
      </button>

      {/* Floating Mega Menu Panel */}
      {isOpen && (
        <div
          ref={menuRef}
          role="region"
          aria-label="PDF Tools Directory"
          className="fixed left-1/2 -translate-x-1/2 top-14 mt-1.5 w-[min(1280px,calc(100vw-1.5rem))] z-50 rounded-2xl border border-border/80 bg-card/98 shadow-2xl backdrop-blur-2xl animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150 text-foreground overflow-hidden max-h-[calc(100vh-4.5rem)] flex flex-col"
        >
          {/* Header Bar: Quick Search + Security Banner */}
          <div className="border-b border-border/60 bg-muted/30 px-6 py-3.5 flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full h-10 pl-10 pr-9 rounded-xl bg-background border border-border/80 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all shadow-2xs"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 rounded-md cursor-pointer"
                  aria-label="Clear search"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 px-3.5 py-1.5 text-xs sm:text-sm font-bold">
                <ShieldCheck className="size-4" />
                <span>{t.privacyBadge}</span>
              </span>
            </div>
          </div>

          {/* Body Content */}
          <div className="overflow-y-auto flex-1 p-6 lg:p-7">
            {/* If user is typing in search, show Instant Search Results */}
            {query.trim().length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
                    {t.searchResults} &ldquo;{query}&rdquo; ({searchResults.length})
                  </p>
                  <Link
                    to={`${baseHref}?q=${encodeURIComponent(query)}`}
                    onClick={() => setIsOpen(false)}
                    className="text-sm font-bold text-primary hover:underline flex items-center gap-1.5"
                  >
                    View in directory <ArrowRight className="size-3.5" />
                  </Link>
                </div>

                {searchResults.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    <p className="text-base font-semibold">{t.noResults}</p>
                    <p className="text-sm mt-1">
                      Try searching with other terms like &ldquo;merge&rdquo;, &ldquo;compress&rdquo;, or &ldquo;sign&rdquo;.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                    {searchResults.map((tool) => (
                      <Link
                        key={tool.slug}
                        to={`${baseHref}/${tool.slug}`}
                        onClick={() => setIsOpen(false)}
                        className="group flex flex-col justify-between p-3.5 rounded-xl border border-border/60 bg-background hover:border-primary/50 hover:bg-secondary/50 transition-all shadow-2xs"
                      >
                        <div className="flex items-start gap-3">
                          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <ToolIcon name={tool.icon} className="size-4.5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors truncate">
                              {tool.name}
                            </p>
                            <span className="inline-block mt-0.5 text-xs text-muted-foreground uppercase font-semibold">
                              {tool.category}
                            </span>
                          </div>
                        </div>
                        {tool.tagline && (
                          <p className="mt-2 text-xs text-muted-foreground line-clamp-1 leading-relaxed">
                            {tool.tagline}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Standard 5-Column Category Layout (FreeConvert Style with clear text) */
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-0 lg:divide-x lg:divide-border/60">
                {columns.map((col, idx) => {
                  const IconCmp = col.icon;
                  return (
                    <div
                      key={col.title}
                      className={`flex flex-col justify-between ${
                        idx > 0 ? "lg:pl-6" : ""
                      } ${idx < columns.length - 1 ? "lg:pr-6" : ""}`}
                    >
                      <div>
                        {/* Column Category Header */}
                        <Link
                          to={`${baseHref}?cat=${encodeURIComponent(col.categoryName)}`}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-2.5 pb-3 mb-3 border-b border-border/60 text-sm sm:text-[15px] font-bold tracking-tight text-foreground hover:text-primary transition-colors group"
                        >
                          <span className={`grid size-7 place-items-center rounded-lg bg-secondary/80 ${col.iconColor}`}>
                            <IconCmp className="size-4" />
                          </span>
                          <span className="truncate">{col.title}</span>
                          <ArrowRight className="size-3.5 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 ml-auto text-primary" />
                        </Link>

                        {/* List of Tools in this Category */}
                        <div className="space-y-1">
                          {col.tools.map((tool) => (
                            <Link
                              key={tool.slug}
                              to={`${baseHref}/${tool.slug}`}
                              onClick={() => setIsOpen(false)}
                              className="group flex items-center justify-between px-3 py-2 rounded-lg text-sm sm:text-[14px] font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
                            >
                              <span className="truncate">{tool.name}</span>
                              {tool.featured && (
                                <span className="size-1.5 rounded-full bg-primary/70 shrink-0 ml-1.5" />
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>

                      {/* View All in Category Link */}
                      <div className="pt-3.5 mt-2 border-t border-border/50">
                        <Link
                          to={`${baseHref}?cat=${encodeURIComponent(col.categoryName)}`}
                          onClick={() => setIsOpen(false)}
                          className="inline-flex items-center gap-1.5 text-xs sm:text-[13px] font-bold text-primary hover:underline"
                        >
                          <span>{t.viewAll} {col.title}</span>
                          <ArrowRight className="size-3" />
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Bottom Action Bar */}
          <div className="border-t border-border/70 bg-secondary/35 px-6 py-3.5 flex flex-wrap items-center justify-between gap-3 text-sm">
            {/* Quick Category Chips */}
            <div className="flex flex-wrap items-center gap-2 max-w-3xl">
              <span className="text-xs sm:text-sm font-bold text-foreground mr-1 hidden sm:inline">
                {t.allCategoriesLabel}
              </span>
              {categories.map((cat) => (
                <Link
                  key={cat}
                  to={`${baseHref}?cat=${encodeURIComponent(cat)}`}
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg border border-border/70 bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all"
                >
                  {cat}
                </Link>
              ))}
            </div>

            {/* Direct Link to PDF Hub */}
            <Link
              to={baseHref}
              onClick={() => setIsOpen(false)}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-xs sm:text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-xs ml-auto"
            >
              <FileText className="size-4" />
              <span>{t.allPdfTools}</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Mobile Drawer Accordion Component for PDF Toolkit.
 * Embedded inside the hamburger drawer in SiteHeader for mobile responsiveness.
 */
interface PdfMobileMenuProps {
  lang?: Language;
  onClose: () => void;
}

export function PdfMobileMenu({ lang = "en", onClose }: PdfMobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [mobileQuery, setMobileQuery] = useState("");

  const t = PDF_MEGA_MENU_I18N[lang] || PDF_MEGA_MENU_I18N.en;
  const { tools, categories, baseHref } = useMemo(() => getPdfData(lang), [lang]);
  const columns = useMemo(() => getPdfColumns(tools, categories), [tools, categories]);

  const filtered = useMemo(() => {
    const q = mobileQuery.trim().toLowerCase();
    if (!q) return [];
    return tools
      .filter((tool) => tool.name.toLowerCase().includes(q) || tool.category.toLowerCase().includes(q))
      .slice(0, 10);
  }, [mobileQuery, tools]);

  return (
    <div className="w-full">
      {/* Mobile Drawer Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl text-sm font-semibold text-foreground transition-colors hover:bg-secondary cursor-pointer"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3">
          <span className="grid size-7 place-items-center rounded-lg bg-primary/10 text-primary">
            <FileText className="size-4" />
          </span>
          <span>{t.pdfToolkit}</span>
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">
            {tools.length}
          </span>
        </div>
        <ChevronDown
          className={`size-4 text-muted-foreground transition-transform duration-200 ${
            isOpen ? "rotate-180 text-primary" : ""
          }`}
        />
      </button>

      {/* Expanded Accordion Content */}
      {isOpen && (
        <div className="mt-1.5 ml-2 pl-3 border-l-2 border-primary/30 space-y-3 py-2 animate-in slide-in-from-top-2 duration-150">
          {/* Quick Hub Link */}
          <Link
            to={baseHref}
            onClick={onClose}
            className="flex items-center justify-between rounded-xl bg-primary/10 p-3 text-xs sm:text-sm font-bold text-primary hover:bg-primary/20 transition-colors"
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="size-4" />
              {t.allPdfTools}
            </span>
            <ArrowRight className="size-4" />
          </Link>

          {/* Quick Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              type="text"
              value={mobileQuery}
              onChange={(e) => setMobileQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full h-9 pl-9 pr-8 rounded-lg bg-background border border-border/80 text-xs sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {mobileQuery && (
              <button
                type="button"
                onClick={() => setMobileQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>

          {/* If search query is present */}
          {mobileQuery.trim().length > 0 ? (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {filtered.map((tool) => (
                <Link
                  key={tool.slug}
                  to={`${baseHref}/${tool.slug}`}
                  onClick={onClose}
                  className="flex items-center justify-between p-2.5 rounded-lg text-xs sm:text-sm font-medium text-foreground bg-background hover:bg-secondary border border-border/60"
                >
                  <span className="truncate">{tool.name}</span>
                  <span className="text-[11px] text-muted-foreground ml-2 shrink-0">{tool.category}</span>
                </Link>
              ))}
              {filtered.length === 0 && (
                <p className="p-3 text-center text-xs text-muted-foreground">{t.noResults}</p>
              )}
            </div>
          ) : (
            /* Category Accordions on Mobile */
            <div className="space-y-2">
              {columns.map((col) => {
                const isCatOpen = activeCategory === col.title;
                const IconCmp = col.icon;
                return (
                  <div key={col.title} className="rounded-lg border border-border/50 bg-background/50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setActiveCategory(isCatOpen ? null : col.title)}
                      className="flex items-center justify-between w-full px-3 py-2.5 text-xs sm:text-sm font-bold text-foreground hover:bg-secondary/60 cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <IconCmp className={`size-4 ${col.iconColor}`} />
                        {col.title}
                      </span>
                      <ChevronDown
                        className={`size-3.5 text-muted-foreground transition-transform duration-200 ${
                          isCatOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isCatOpen && (
                      <div className="px-2.5 pb-2.5 pt-1 border-t border-border/40 space-y-1 bg-secondary/20">
                        {col.tools.map((tool) => (
                          <Link
                            key={tool.slug}
                            to={`${baseHref}/${tool.slug}`}
                            onClick={onClose}
                            className="block px-2.5 py-2 rounded-md text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors truncate"
                          >
                            {tool.name}
                          </Link>
                        ))}
                        <Link
                          to={`${baseHref}?cat=${encodeURIComponent(col.categoryName)}`}
                          onClick={onClose}
                          className="inline-flex items-center gap-1 px-2.5 pt-1.5 text-xs font-bold text-primary hover:underline"
                        >
                          {t.viewAll} {col.title} →
                        </Link>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
